import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";
import { ChevronDown } from "lucide-react";
import ExploreGallery from "@/components/javits-b2b/ExploreGallery";

// Image imports
import heroFallback from "@/assets/javits-b2b/hero-fallback.jpg";
import exteriorClean from "@/assets/javits-b2b/exterior-clean.png";
import exteriorBranded from "@/assets/javits-b2b/exterior-branded.png";
import interiorClean from "@/assets/javits-b2b/interior-clean.png";
import interiorBranded from "@/assets/javits-b2b/interior-branded.png";
import podRow from "@/assets/javits-b2b/pod-row.png";
import podStairs from "@/assets/javits-b2b/pod-stairs.png";
import podsShowcase1 from "@/assets/javits-b2b/pods-showcase-1.jpg";
import podsShowcase2 from "@/assets/javits-b2b/pods-showcase-2.jpg";
import podsShowcase3 from "@/assets/javits-b2b/pods-showcase-3.jpg";
import sponsorship1 from "@/assets/javits-b2b/sponsorship-1.png";
import sponsorship2 from "@/assets/javits-b2b/sponsorship-2.png";
import sponsorship3 from "@/assets/javits-b2b/sponsorship-3.png";
import sponsorship4 from "@/assets/javits-b2b/sponsorship-4.png";

const JavitsB2B = () => {
  const [exteriorSlider, setExteriorSlider] = useState([0]);
  const [interiorSlider, setInteriorSlider] = useState([0]);
  const [formData, setFormData] = useState({
    name: "",
    eventName: "",
    eventDates: "",
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const quietCornerRef = useRef<HTMLDivElement>(null);
  const buyoutPackagesRef = useRef<HTMLDivElement>(null);

  const scrollToQuietCorner = () => {
    quietCornerRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToBuyoutPackages = () => {
    buyoutPackagesRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Placeholder for form submission
    await new Promise((resolve) => setTimeout(resolve, 1000));
    alert("Thank you! We'll be in touch about your buyout quote.");
    setFormData({ name: "", eventName: "", eventDates: "", email: "" });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-black text-white font-inter">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background with Fallback */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            poster={heroFallback}
          >
            <source src="/javits-b2b/hero-video.mp4" type="video/mp4" />
          </video>
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto flex flex-col h-full justify-between py-24">
          <div className="mt-8">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-light tracking-tight mb-6">
              Turnkey Workstation Lounge Activation
            </h1>
            <p className="text-xl md:text-2xl font-light text-white/80 max-w-2xl mx-auto">
              Six premium, soundproof pods. One exclusive buyout. The ultimate amenity for your attendees.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToBuyoutPackages}
              className="bg-white text-black hover:bg-white/90 px-8 py-6 text-lg font-medium rounded-full transition-all duration-300"
            >
              View Buyout Packages
            </Button>
            <Button
              onClick={scrollToQuietCorner}
              variant="outline"
              className="border-white bg-white/10 text-white hover:bg-white/20 px-8 py-6 text-lg font-medium rounded-full transition-all duration-300"
            >
              View Quiet Corner Pods
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Value Proposition Section */}
      {/* Quiet Corner Pods Section */}
      <section ref={quietCornerRef} className="py-24 md:py-32 px-6 bg-neutral-950">
        <div className="max-w-6xl mx-auto">
          <p className="text-white/50 text-sm uppercase tracking-widest text-center mb-4">
            Quiet Corner Pods
          </p>
          <h2 className="text-3xl md:text-5xl font-light text-center mb-12 tracking-tight">
            Keep your audience at the event with Quiet Corner pods
          </h2>
          
          {/* Three Images Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={podsShowcase1} 
                alt="Quiet Corner pods deployed at event venue"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={podsShowcase2} 
                alt="Person working inside a Quiet Corner pod"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={podsShowcase3} 
                alt="Two people meeting inside a Quiet Corner pod"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <p className="text-lg text-white/70 leading-relaxed">
              Escape the floor noise with our soundproof, tech-enabled pods that provide a premium sanctuary for critical calls/meetings and deep focus in the heart of the event.
            </p>
            <p className="text-lg text-white/70 leading-relaxed">
              Our pods can be booked seamlessly on demand via our kiosk tap to pay technology on each pod or in advance at{" "}
              <a 
                href="https://www.quietcorner.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white underline underline-offset-4 hover:text-white/80 transition-colors"
              >
                www.quietcorner.io
              </a>.
            </p>
            <p className="text-lg text-white/70 leading-relaxed">
              Pod sizes range from single-person to large 4-person meeting pods.
            </p>
          </div>
        </div>
      </section>

      {/* View Buyout Packages Section */}
      <section ref={buyoutPackagesRef} className="py-24 md:py-32 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <p className="text-white/50 text-sm uppercase tracking-widest text-center mb-4">
            View Buyout Packages
          </p>
          <h2 className="text-3xl md:text-5xl font-light text-center mb-16 tracking-tight">
            Elevate attendee satisfaction while driving brand value
          </h2>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Buyout Opportunities */}
            <div className="space-y-8">
              <h3 className="text-2xl font-light text-white/90">Buyout Opportunities</h3>
              <div className="space-y-6">
                <div className="p-6 rounded-2xl bg-neutral-900/50 border border-white/10">
                  <h4 className="text-lg font-medium text-white mb-2">Exclusive Access</h4>
                  <p className="text-white/60">
                    Private pods reserved only for event attendees
                  </p>
                </div>
                <div className="p-6 rounded-2xl bg-neutral-900/50 border border-white/10">
                  <h4 className="text-lg font-medium text-white mb-2">Seamless Setup</h4>
                  <p className="text-white/60">
                    Dedicated booking links, promo codes & flexible placement in or near the event footprint
                  </p>
                </div>
              </div>
            </div>

            {/* Sponsorship Opportunities */}
            <div className="space-y-8">
              <h3 className="text-2xl font-light text-white/90">Sponsorship Opportunities</h3>
              <div className="space-y-6">
                <div className="p-6 rounded-2xl bg-neutral-900/50 border border-white/10">
                  <h4 className="text-lg font-medium text-white mb-2">Own The Experience</h4>
                  <p className="text-white/60">
                    Pair a pod buyout with a high-impact brand activation designed to drive visibility & engagement
                  </p>
                </div>
                <div className="p-6 rounded-2xl bg-neutral-900/50 border border-white/10">
                  <h4 className="text-lg font-medium text-white mb-2">Premium Brand Touchpoints</h4>
                  <p className="text-white/60">
                    Exterior wraps, branded pod screens, interior decals, & floor or entry clings
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sponsorship Images */}
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={sponsorship1} 
                alt="Shopify branded pods near charging area"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={sponsorship2} 
                alt="Shopify Retail Lounge main hall activation"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={sponsorship3} 
                alt="Branded pods near stairs"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={sponsorship4} 
                alt="Branded pod interior views"
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          {/* Buyout Package Cards */}
          <div className="mt-24">
            <h3 className="text-2xl md:text-3xl font-light text-center mb-12 tracking-tight">
              Choose Your Package
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Quiet Corner Buyout */}
              <div className="relative p-6 rounded-2xl bg-neutral-900/50 border border-white/10 flex flex-col">
                <h4 className="text-xl font-medium text-white mb-2">Quiet Corner Buyout</h4>
                <div className="mb-4">
                  <span className="text-2xl font-light text-white">$12,000</span>
                  <span className="text-white/50 text-sm ml-2">total</span>
                </div>
                <p className="text-white/60 text-sm mb-6 flex-grow">
                  Exclusive access to all six Quiet Corner pods for your event. Relocated into your exhibit hall and free for attendees—or used however you choose.
                </p>
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-3">Includes</p>
                  <p className="text-white/70 text-sm">• Full pod buyout</p>
                  <p className="text-white/70 text-sm">• Exhibit hall placement</p>
                  <p className="text-white/70 text-sm">• Unlimited attendee or VIP use</p>
                </div>
              </div>

              {/* Screen Takeover */}
              <div className="relative p-6 rounded-2xl bg-neutral-900/50 border border-white/10 flex flex-col">
                <h4 className="text-xl font-medium text-white mb-2">Screen Takeover</h4>
                <div className="mb-4">
                  <span className="text-2xl font-light text-white">$15,000</span>
                  <span className="text-white/50 text-sm ml-2">total</span>
                </div>
                <p className="text-white/60 text-sm mb-6 flex-grow">
                  Everything in the Buyout—plus full digital branding on every pod.
                </p>
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-3">Includes</p>
                  <p className="text-white/70 text-sm">• Buyout of all pods</p>
                  <p className="text-white/70 text-sm">• Full screen branding takeover</p>
                  <p className="text-white/70 text-sm">• Sponsor or exhibitor messaging</p>
                </div>
              </div>

              {/* Brand Wrap */}
              <div className="relative p-6 rounded-2xl bg-neutral-900/50 border border-white/20 flex flex-col">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-white text-black text-xs font-medium rounded-full">
                  Popular
                </div>
                <h4 className="text-xl font-medium text-white mb-2">Brand Wrap</h4>
                <div className="mb-4">
                  <span className="text-2xl font-light text-white">$25,000</span>
                  <span className="text-white/50 text-sm ml-2">total</span>
                </div>
                <p className="text-white/60 text-sm mb-6 flex-grow">
                  Turn Quiet Corner into a show-floor statement.
                </p>
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-3">Includes</p>
                  <p className="text-white/70 text-sm">• Buyout + screen takeover</p>
                  <p className="text-white/70 text-sm">• Custom exterior vinyl wraps</p>
                  <p className="text-white/70 text-sm">• High-visibility physical branding</p>
                </div>
              </div>

              {/* Platinum Takeover */}
              <div className="relative p-6 rounded-2xl bg-gradient-to-b from-neutral-800/80 to-neutral-900/80 border border-white/20 flex flex-col">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-200 to-yellow-400 text-black text-xs font-medium rounded-full">
                  Premium
                </div>
                <h4 className="text-xl font-medium text-white mb-2">Platinum Takeover</h4>
                <div className="mb-4">
                  <span className="text-2xl font-light text-white">$35,000</span>
                  <span className="text-white/50 text-sm ml-2">total</span>
                </div>
                <p className="text-white/60 text-sm mb-6 flex-grow">
                  The ultimate Quiet Corner brand experience.
                </p>
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-3">Includes</p>
                  <p className="text-white/70 text-sm">• Buyout + screens + wraps</p>
                  <p className="text-white/70 text-sm">• Branded floor mats (inside & out)</p>
                  <p className="text-white/70 text-sm">• Custom seat fabric color</p>
                  <p className="text-white/70 text-sm">• Billboard signage above pods</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore the Experience Gallery */}
      <ExploreGallery />

      {/* Sponsorship Simulator Section */}
      <section className="py-24 md:py-32 px-6 bg-neutral-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light tracking-tight mb-4">
              Total Brand Takeover.
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Toggle below to see how we turn standard infrastructure into a premium branded lounge.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Slider A: Exterior */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-center text-white/80">
                Exterior Branding
              </h3>
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-900 border border-white/10">
                {/* Clean State */}
                <img
                  src={exteriorClean}
                  alt="Clean exterior pods"
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                  style={{ opacity: 1 - exteriorSlider[0] / 100 }}
                />
                {/* Branded State */}
                <img
                  src={exteriorBranded}
                  alt="Branded exterior pods - Shopify Retail Lounge"
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                  style={{ opacity: exteriorSlider[0] / 100 }}
                />
              </div>
              <div className="px-4">
                <div className="flex justify-between text-sm text-white/50 mb-2">
                  <span>Clean</span>
                  <span>Branded</span>
                </div>
                <Slider
                  value={exteriorSlider}
                  onValueChange={setExteriorSlider}
                  max={100}
                  step={1}
                  className="cursor-pointer"
                />
              </div>
            </div>

            {/* Slider B: Interior */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-center text-white/80">
                Interior Experience
              </h3>
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-neutral-900 border border-white/10">
                {/* Clean State */}
                <img
                  src={interiorClean}
                  alt="Clean interior pods"
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                  style={{ opacity: 1 - interiorSlider[0] / 100 }}
                />
                {/* Branded State */}
                <img
                  src={interiorBranded}
                  alt="Branded interior pods with green lighting and logos"
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                  style={{ opacity: interiorSlider[0] / 100 }}
                />
              </div>
              <div className="px-4">
                <div className="flex justify-between text-sm text-white/50 mb-2">
                  <span>Clean</span>
                  <span>Branded</span>
                </div>
                <Slider
                  value={interiorSlider}
                  onValueChange={setInteriorSlider}
                  max={100}
                  step={1}
                  className="cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Fleet Section */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center mb-6 tracking-tight">
            The Fleet
          </h2>
          <p className="text-center text-white/60 mb-16 max-w-xl mx-auto">
            Fully equipped with power, lighting, and ventilation.
          </p>

          {/* Fleet Images */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={podRow} 
                alt="Four focus pods in a row with plants"
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img 
                src={podStairs} 
                alt="Branded pods deployed near stairs at Javits"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          {/* Fleet Details */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-center">
            <div className="px-8 py-4 rounded-full bg-neutral-900/50 border border-white/10">
              <p className="text-white/80 font-medium">4× Focus Pods</p>
              <p className="text-white/50 text-sm">Single Occupancy</p>
            </div>
            <div className="px-8 py-4 rounded-full bg-neutral-900/50 border border-white/10">
              <p className="text-white/80 font-medium">2× Meeting Pods</p>
              <p className="text-white/50 text-sm">Dual Occupancy</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs Section */}
      <section className="py-24 md:py-32 px-6 bg-neutral-950">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center mb-16 tracking-tight">
            Technical Specifications
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="dimensions" className="border border-white/10 rounded-xl px-6 bg-neutral-900/30">
              <AccordionTrigger className="text-white/90 hover:text-white py-6 text-left">
                Dimensions & Footprint
              </AccordionTrigger>
              <AccordionContent className="text-white/60 pb-6">
                <div className="space-y-2">
                  <p><strong>Focus Pod:</strong> 48"W × 48"D × 90"H (4 sq ft footprint)</p>
                  <p><strong>Meeting Pod:</strong> 72"W × 48"D × 90"H (6 sq ft footprint)</p>
                  <p><strong>Weight:</strong> ~400 lbs per pod (castered for easy mobility)</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="power" className="border border-white/10 rounded-xl px-6 bg-neutral-900/30">
              <AccordionTrigger className="text-white/90 hover:text-white py-6 text-left">
                Power & Connectivity
              </AccordionTrigger>
              <AccordionContent className="text-white/60 pb-6">
                <div className="space-y-2">
                  <p><strong>Power:</strong> Standard 110V outlet required per pod</p>
                  <p><strong>Internal:</strong> USB-A, USB-C charging ports</p>
                  <p><strong>Lighting:</strong> Adjustable LED ambient lighting</p>
                  <p><strong>Ventilation:</strong> Whisper-quiet HVAC with fresh air circulation</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="branding" className="border border-white/10 rounded-xl px-6 bg-neutral-900/30">
              <AccordionTrigger className="text-white/90 hover:text-white py-6 text-left">
                Branding Surfaces
              </AccordionTrigger>
              <AccordionContent className="text-white/60 pb-6">
                <div className="space-y-2">
                  <p><strong>Exterior:</strong> Full vinyl wrap capability on all exterior panels</p>
                  <p><strong>Interior Screens:</strong> Digital displays for branded content loops</p>
                  <p><strong>Ambient Lighting:</strong> Customizable RGB to match brand colors</p>
                  <p><strong>Signage:</strong> Optional exterior topper and floor decals</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Footer & Conversion Section */}
      <section className="py-24 md:py-32 px-6 bg-black">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-light tracking-tight mb-4">
            Reserve the Fleet.
          </h2>
          <p className="text-white/60 mb-12">
            Secure exclusive access to the Quiet Corner fleet for your next event.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6 text-left">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white/70">Your Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-neutral-900 border-white/10 text-white placeholder:text-white/30 focus:border-white/30 rounded-xl h-12"
                  placeholder="Jane Smith"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-neutral-900 border-white/10 text-white placeholder:text-white/30 focus:border-white/30 rounded-xl h-12"
                  placeholder="jane@company.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="eventName" className="text-white/70">Event Name</Label>
              <Input
                id="eventName"
                value={formData.eventName}
                onChange={(e) => setFormData({ ...formData, eventName: e.target.value })}
                required
                className="bg-neutral-900 border-white/10 text-white placeholder:text-white/30 focus:border-white/30 rounded-xl h-12"
                placeholder="NRF 2025"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eventDates" className="text-white/70">Event Dates</Label>
              <Input
                id="eventDates"
                value={formData.eventDates}
                onChange={(e) => setFormData({ ...formData, eventDates: e.target.value })}
                required
                className="bg-neutral-900 border-white/10 text-white placeholder:text-white/30 focus:border-white/30 rounded-xl h-12"
                placeholder="January 12-14, 2025"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-white text-black hover:bg-white/90 h-14 text-lg font-medium rounded-full mt-8 transition-all duration-300"
            >
              {isSubmitting ? "Sending..." : "Request Buyout Quote"}
            </Button>
          </form>
        </div>
      </section>

      {/* Copyright Footer */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Quiet Corner IO. All rights reserved.
          </p>
          <a
            href="https://quietcorner.io"
            className="text-white/40 hover:text-white/60 text-sm transition-colors"
          >
            quietcorner.io
          </a>
        </div>
      </footer>
    </div>
  );
};

export default JavitsB2B;
